import { Link } from "@heroui/link";
import { m } from "framer-motion";
import { useLocation } from "react-router-dom";
import { useEffect } from "react";

import { Navbar } from "@/components/navbar";
import { LinkedInIcon } from "@/components/icons";
import Seo from "@/components/Seo";
import { useSeoSerpSettings } from "@/api/useSeoSerpSettings";
import { siteConfig } from "@/config/site";
import { getDefaultSeoSerpTemplate } from "@/config/seoSerpDefaults";
import { teacherFeatures } from "@/data/teacherFeatures";
import { titleFromSlug } from "@/utils/seo";
import {
  applySeoTemplate,
  buildBreadcrumbJsonLd,
  sanitizeSeoPathValue,
} from "@/utils/seoSerp";
import { trackActivityEvent } from "@/utils/activityTracking";

// Chemins exacts → clé de gabarit SERP (backend/src/config/seoSerp.js).
// Les routes dynamiques sont résolues plus bas, à partir des segments.
const STATIC_SERP_PAGE_TYPES = new Map<string, string>([
  ["/", "home"],
  ["/tuteurs", "tutorsIndex"],
  ["/blog", "blogIndex"],
  ["/press-releases", "pressReleasesIndex"],
  ["/devenir-professeur", "becomeTeacher"],
  ["/proflinker", "profLinker"],
  ["/decouvrir-proflinker", "discoverProfLinker"],
  ["/contact", "contact"],
  ["/demande", "demande"],
  ["/mentions-legales", "legalNotice"],
  ["/cgu", "cgu"],
  ["/connexion", "login"],
  ["/inscription", "signup"],
]);

// Routes dynamiques : la clé de gabarit se déduit des segments de chemin.
function resolveSerpPageType(pathname: string, segments: string[]) {
  const staticPageType = STATIC_SERP_PAGE_TYPES.get(pathname);

  if (staticPageType) return staticPageType;
  if (segments[0] === "blog" && segments[1]) return "blogPost";
  if (segments[0] === "press-releases" && segments[1]) {
    return "pressReleasePost";
  }
  if (
    segments[0] === "devenir-professeur" &&
    segments[1] === "fonctionnalites" &&
    segments[2]
  ) {
    return "becomeTeacherFeature";
  }
  if (segments[0] === "cours") {
    if (segments.length === 2) return "courseSingle";
    if (segments.length === 3) return "courseCitySubject";
    if (segments.length >= 4) return "courseTutor";
  }

  return "";
}

export default function DefaultLayout({
  children,
  wide = false,
  fullBleed = false,
}: {
  children: React.ReactNode;
  wide?: boolean;
  fullBleed?: boolean;
}) {
  const location = useLocation();
  const shouldNoIndexParameterizedUrl = location.search.trim().length > 0;
  const appEnv = import.meta.env.VITE_APP_ENV ?? import.meta.env.MODE;
  const isTest = appEnv === "test";
  const buildVersion = import.meta.env.VITE_BUILD_VERSION ?? "unknown";
  const routeTitleMap = new Map<string, string>([
    ["/", "Accueil"],
    ["/tuteurs", "Professeurs"],
    ["/blog", "Blog"],
    ["/press-releases", "Communiqués de presse"],
    ["/devenir-professeur", "Devenir professeur"],
    ["/decouvrir-proflinker", "Découvrir ProfLinker"],
    ["/proflinker", "ProfLinker"],
    ["/contact", "Contact"],
    ["/demande", "Demande de professeur"],
    ["/tableau-de-bord", "Tableau de bord"],
    ["/onboarding", "Onboarding"],
    ["/connexion", "Connexion"],
    ["/inscription", "Inscription"],
    ["/verification-email", "Vérification email"],
    ["/mot-de-passe-oublie", "Mot de passe oublié"],
    ["/reset-password", "Réinitialiser le mot de passe"],
    ["/mentions-legales", "Mentions légales"],
    ["/cgu", "CGU"],
    ["/admin", "Admin"],
    ["/deconnexion", "Déconnexion"],
    ["/pricing", "Tarifs"],
    ["/docs", "Documentation"],
    ["/about", "À propos"],
  ]);
  const pathname = location.pathname;
  const seoSerpSettings = useSeoSerpSettings();
  const defaultTitle =
    routeTitleMap.get(pathname) ??
    (pathname.startsWith("/cours-particuliers")
      ? "Cours particuliers"
      : pathname.startsWith("/cours/")
        ? "Cours particuliers"
        : pathname.startsWith("/profil/")
          ? "Profil"
          : pathname.startsWith("/blog/")
            ? "Blog"
            : pathname.startsWith("/press-releases/")
              ? "Communiqués de presse"
              : "Franc-Succès");
  const segments = pathname
    .split("/")
    .filter(Boolean)
    .map((segment) => decodeURIComponent(segment));

  const serpPageType = resolveSerpPageType(pathname, segments);

  const slugLabel = segments[1] ? titleFromSlug(segments[1]) : "";
  const slug = segments[1] ?? "";
  const city = segments[1] ? titleFromSlug(segments[1]) : "";
  const citySlug = segments[1] ?? "";
  const subject = segments[2] ? titleFromSlug(segments[2]) : "";
  const subjectSlug = segments[2] ?? "";
  const tutorSlug = segments[3] ?? "";
  const tutorName = tutorSlug
    ? tutorSlug
        .split("-")
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ")
    : "";
  const listingTitle = tutorSlug
    ? tutorSlug
        .split("-")
        .slice(2)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ")
    : "";
  const postTitle = segments[1] ? titleFromSlug(segments[1]) : "";
  const postSlug = segments[1] ?? "";
  const pressReleaseTitle = segments[1] ? titleFromSlug(segments[1]) : "";
  const pressReleaseSlug = segments[1] ?? "";
  const featureSlug = segments[2] ?? "";
  const featureTitle =
    teacherFeatures.find((feature) => feature.slug === featureSlug)?.title ??
    (featureSlug ? titleFromSlug(featureSlug) : "");
  // Les réglages administrables ne sont chargés que côté client : sans le
  // gabarit par défaut, le HTML servi aux moteurs porterait la description
  // générique de siteConfig sur toutes les pages.
  const template = serpPageType
    ? (seoSerpSettings?.templates?.[serpPageType] ??
      getDefaultSeoSerpTemplate(serpPageType))
    : undefined;
  const templateVariables = {
    siteName: siteConfig.name,
    slugLabel,
    slug,
    city,
    citySlug,
    subject,
    subjectSlug,
    tutorName,
    tutorSlug,
    listingTitle,
    postTitle,
    postSlug,
    pressReleaseTitle,
    pressReleaseSlug,
    featureTitle,
    featureSlug,
  };
  const seoTitle = template?.title
    ? applySeoTemplate(template.title, templateVariables)
    : defaultTitle;
  const seoDescription = template?.description
    ? applySeoTemplate(template.description, templateVariables)
    : undefined;
  const seoCanonicalPath = template?.canonicalPath
    ? sanitizeSeoPathValue(
        applySeoTemplate(template.canonicalPath, templateVariables),
      )
    : undefined;
  const seoBreadcrumbJsonLd = template?.breadcrumbTrail
    ? buildBreadcrumbJsonLd(template.breadcrumbTrail, templateVariables)
    : null;

  useEffect(() => {
    trackActivityEvent({
      eventType: "page_view",
      path: `${location.pathname}${location.search}`,
      metadata: {
        title: document.title,
      },
    });
  }, [location.pathname, location.search]);

  return (
    <div className="relative flex min-h-dvh flex-col">
      <Seo
        canonicalPath={seoCanonicalPath}
        description={seoDescription}
        isHomePage={pathname === "/"}
        jsonLd={seoBreadcrumbJsonLd ?? undefined}
        noIndex={shouldNoIndexParameterizedUrl}
        title={seoTitle || defaultTitle}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 hidden dark:block bg-canvas-grain"
      />
      <Navbar />
      <m.main
        animate={{ opacity: 1 }}
        className={
          fullBleed
            ? "flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-x-clip"
            : wide
              ? "container mx-auto flex min-h-0 min-w-0 max-w-7xl flex-1 flex-col overflow-x-clip px-4 sm:px-6 sm:pt-16"
              : "container mx-auto flex min-h-0 min-w-0 max-w-6xl flex-1 flex-col overflow-x-clip px-4 sm:px-6 sm:pt-16 lg:px-8"
        }
        initial={{ opacity: 0 }}
        transition={{ duration: 0.35, ease: "easeOut" }}
      >
        {children}
      </m.main>
      <footer className="flex-shrink-0 w-full flex flex-col items-center justify-center gap-2 py-6 px-4">
        {isTest && (
          <span className="rounded-full bg-warning-100 px-3 py-1 text-xs text-warning-700">
            Test · build {buildVersion}
          </span>
        )}
        <Link
          className="flex items-center gap-2 text-current"
          href="/"
          title="Franc-Succès"
        >
          <span className="text-default-600 text-center">
            Franc-Succès, plateforme de professeurs particuliers
          </span>
        </Link>
        <nav className="flex flex-row flex-wrap items-center justify-center gap-3 sm:gap-4 text-sm text-default-500">
          <Link className="text-default-500" href="/mentions-legales">
            Mentions légales
          </Link>
          <Link className="text-default-500" href="/cgu">
            CGU
          </Link>
          <Link className="text-default-500" href="/press-releases">
            Communiqués de presse
          </Link>
          <Link
            isExternal
            className="inline-flex items-center gap-2 rounded-md bg-content2 px-3 py-1.5 text-foreground transition-colors hover:bg-content3"
            href="https://www.linkedin.com/company/franc-succes-tutorat"
            title="Franc-Succès sur LinkedIn"
          >
            <LinkedInIcon className="text-foreground/80" size={18} />
            LinkedIn
          </Link>
        </nav>
      </footer>
    </div>
  );
}
